import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

/**
 * Address-book section shown inside the Customer edit dialog. Manages
 * the customer's saved pickup/delivery locations — these auto-appear in
 * the Job form's location dropdowns whenever this customer is selected.
 */
export default function CustomerLocationsSection({ customerId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const emptyRow = {
    label: "",
    kind: "both",
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    contact_name: "",
    contact_phone: "",
  };
  const [row, setRow] = useState(emptyRow);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/locations`);
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load locations");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const create = async () => {
    if (!row.label.trim()) { toast.error("Give this location a name"); return; }
    if (!row.street && !row.city) { toast.error("Enter at least a street or city"); return; }
    setSaving(true);
    try {
      await api.post("/locations", {
        ...row,
        label: row.label.trim(),
        customer_id: customerId,
      });
      toast.success("Location saved");
      setRow(emptyRow);
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Remove "${loc.label}" from this customer's address book?`)) return;
    try {
      await api.delete(`/locations/${loc.id}`);
      toast.success("Location removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove");
    }
  };

  const kindLabel = (k) => k === "pickup" ? "PICKUP" : k === "delivery" ? "DELIVERY" : "BOTH";
  const kindColor = (k) => k === "pickup" ? "bg-blue-100 text-blue-800"
    : k === "delivery" ? "bg-emerald-100 text-emerald-800"
    : "bg-zinc-200 text-zinc-800";

  return (
    <div className="border-t border-zinc-200 pt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="mono-label">SAVED PICKUP / DELIVERY LOCATIONS</div>
        {!showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="text-[#0056B3] text-xs inline-flex items-center gap-1 hover:underline"
            data-testid="cust-add-location-btn"
          >
            <Plus className="h-3 w-3" /> Add location
          </button>
        )}
      </div>
      <p className="text-[11px] text-zinc-600 mb-3">
        Locations added here appear as one-click options when creating a job for this customer.
      </p>

      {items.length > 0 && (
        <div className="border border-zinc-200 divide-y divide-zinc-200 mb-3">
          {items.map((l) => (
            <div key={l.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`cust-loc-${l.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 text-[9px] font-mono ${kindColor(l.kind)}`}>{kindLabel(l.kind)}</span>
                  <span className="font-semibold truncate">{l.label}</span>
                </div>
                <div className="text-[11px] text-zinc-600 truncate">
                  {[l.name, l.street, l.city, l.state, l.zip].filter(Boolean).join(", ")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(l)}
                className="p-1 text-zinc-500 hover:text-rose-700"
                title="Remove"
                data-testid={`cust-loc-remove-${l.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {!items.length && !loading && !showAdd && (
        <div className="border border-dashed border-zinc-300 px-3 py-4 text-center text-xs text-zinc-500">
          No saved locations yet. Click <b>Add location</b> to create the customer&apos;s first pickup or delivery site.
        </div>
      )}

      {showAdd && (
        <div className="border border-zinc-950 bg-zinc-50 p-3 space-y-2" data-testid="cust-loc-form">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="mono-label">Location name *</label>
              <Input
                value={row.label}
                onChange={(e) => setRow({ ...row, label: e.target.value })}
                className="sharp mt-1 apex-input bg-white"
                placeholder="e.g. Main Warehouse Delta"
                data-testid="cust-loc-label"
              />
            </div>
            <div>
              <label className="mono-label">Use for</label>
              <Select value={row.kind} onValueChange={(v) => setRow({ ...row, kind: v })}>
                <SelectTrigger className="sharp mt-1 h-9 bg-white" data-testid="cust-loc-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup">Pickup only</SelectItem>
                  <SelectItem value="delivery">Delivery only</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="mono-label">Company / site name</label>
            <Input value={row.name} onChange={(e) => setRow({ ...row, name: e.target.value })} className="sharp mt-1 apex-input bg-white" data-testid="cust-loc-name" />
          </div>
          <div>
            <label className="mono-label">Street</label>
            <Input value={row.street} onChange={(e) => setRow({ ...row, street: e.target.value })} className="sharp mt-1 apex-input bg-white" data-testid="cust-loc-street" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="col-span-2">
              <label className="mono-label">City</label>
              <Input value={row.city} onChange={(e) => setRow({ ...row, city: e.target.value })} className="sharp mt-1 apex-input bg-white" data-testid="cust-loc-city" />
            </div>
            <div>
              <label className="mono-label">State / Prov</label>
              <Input value={row.state} onChange={(e) => setRow({ ...row, state: e.target.value })} className="sharp mt-1 apex-input font-mono uppercase bg-white" maxLength={3} />
            </div>
            <div>
              <label className="mono-label">Zip / Postal</label>
              <Input value={row.zip} onChange={(e) => setRow({ ...row, zip: e.target.value })} className="sharp mt-1 apex-input font-mono bg-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mono-label">Contact name</label>
              <Input value={row.contact_name} onChange={(e) => setRow({ ...row, contact_name: e.target.value })} className="sharp mt-1 apex-input bg-white" />
            </div>
            <div>
              <label className="mono-label">Contact phone</label>
              <Input value={row.contact_phone} onChange={(e) => setRow({ ...row, contact_phone: e.target.value })} className="sharp mt-1 apex-input bg-white" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" className="sharp border-zinc-300 h-8 text-xs" onClick={() => { setShowAdd(false); setRow(emptyRow); }}>
              Cancel
            </Button>
            <Button type="button" disabled={saving} onClick={create} className="sharp bg-[#0056B3] hover:bg-[#004494] h-8 text-xs" data-testid="cust-loc-save">
              {saving ? "Saving…" : "Save location"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
